import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase as cloudClient, supabase } from '@/lib/supabase';
import { logAdminAction } from '@/lib/audit-log';
import { runPipeline, getPipelineStatus, getPipelineRuns, type PipelineRun } from '@/lib/pipeline-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Play, Loader2, CheckCircle2, Circle, Mail, Rocket, ThumbsUp, ThumbsDown, ExternalLink, Search, ArrowUpDown, ArrowUp, ArrowDown, Download, Trash2, AlertTriangle } from 'lucide-react';
import NewsletterQueue from '@/components/NewsletterQueue';
import { DataPagination } from '@/components/DataPagination';

type InsightSortConfig = { key: string; direction: 'asc' | 'desc' } | null;

export default function WeeklyTop100() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [runStartTime, setRunStartTime] = useState<number | null>(null);

  const POLLING_TIMEOUT = 10 * 60 * 1000; // 10 minutes

  // Poll active run status
  const { data: activeRun } = useQuery({
    queryKey: ['pipeline-status', activeRunId],
    queryFn: () => getPipelineStatus(activeRunId!),
    enabled: !!activeRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;

      // Check for timeout
      if (runStartTime && Date.now() - runStartTime > POLLING_TIMEOUT) {
        return false;
      }

      return 10_000;
    },
  });

  useEffect(() => {
    if (activeRun?.status === 'completed') {
      toast.success('Pipeline completed!');
      queryClient.invalidateQueries({ queryKey: ['pipeline-runs'] });
      setActiveRunId(null);
      setRunStartTime(null);
    } else if (activeRun?.status === 'failed') {
      toast.error(`Pipeline failed: ${activeRun.error || 'Unknown error'}`);
      setActiveRunId(null);
      setRunStartTime(null);
    }
  }, [activeRun?.status, queryClient]);

  // Check for timeout
  useEffect(() => {
    if (!activeRunId || !runStartTime) return;

    const checkTimeout = setInterval(() => {
      if (Date.now() - runStartTime > POLLING_TIMEOUT) {
        toast.error('Pipeline timed out. The operation took too long.');
        setActiveRunId(null);
        setRunStartTime(null);
        queryClient.invalidateQueries({ queryKey: ['pipeline-runs'] });
      }
    }, 10_000);

    return () => clearInterval(checkTimeout);
  }, [activeRunId, runStartTime, POLLING_TIMEOUT, queryClient]);

  const { data: runs = [], isLoading: runsLoading } = useQuery({
    queryKey: ['pipeline-runs'],
    queryFn: async () => {
      const data = await getPipelineRuns()
      if (data?.length) console.log('[pipeline-runs] first row keys:', Object.keys(data[0]), data[0])
      return data
    },
  });

  // Run delete state
  const [confirmDeleteRunId, setConfirmDeleteRunId] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: async (runId: string) => {
      // Delete all trending_videos linked to this run and capture count
      const { data: deletedVideos, error: videosError } = await supabase
        .from('trending_videos')
        .delete()
        .eq('pipeline_run_id', runId)
        .select('id')
      if (videosError) throw videosError

      // Delete the pipeline run record
      const { error: runError } = await supabase
        .from('pipeline_runs')
        .delete()
        .eq('id', runId)
      if (runError) throw runError

      return { videosDeleted: deletedVideos?.length ?? 0 }
    },
    onSuccess: (data, runId) => {
      toast.success('Run and all associated videos deleted')
      queryClient.invalidateQueries({ queryKey: ['pipeline-runs'] })
      setConfirmDeleteRunId(null)

      void logAdminAction({
        action:    'DELETE',
        tableName: 'pipeline_runs',
        recordId:  runId,
        metadata:  { event: 'pipeline_run_deleted', run_id: runId, videos_deleted: data?.videosDeleted ?? 0 },
      })
    },
    onError: (err: any) => {
      toast.error(err.message ?? 'Delete failed')
      setConfirmDeleteRunId(null)
    },
  })

  // Run history search / sort / pagination (client-side — data from Railway API)
  const [runSearch, setRunSearch] = useState('');
  const [runSortKey, setRunSortKey] = useState<'file_name' | 'status' | 'created_at'>('created_at');
  const [runSortDir, setRunSortDir] = useState<'asc' | 'desc'>('desc');
  const [runPage, setRunPage] = useState(1);
  const [runPageSize, setRunPageSize] = useState(10);
  const [runExporting, setRunExporting] = useState(false);

  const handleRunSort = (key: 'file_name' | 'status' | 'created_at') => {
    if (runSortKey === key) {
      setRunSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setRunSortKey(key);
      setRunSortDir('desc');
    }
    setRunPage(1);
  };

  const getRunSortIcon = (key: string) => {
    if (runSortKey !== key) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    return runSortDir === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const filteredRuns = runs
    .filter((r: PipelineRun) => {
      const term = runSearch.trim().toLowerCase();
      if (!term) return true;
      return (
        (r.file_name || '').toLowerCase().includes(term) ||
        r.status.toLowerCase().includes(term)
      );
    })
    .sort((a: PipelineRun, b: PipelineRun) => {
      const av = (a as any)[runSortKey] ?? '';
      const bv = (b as any)[runSortKey] ?? '';
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return runSortDir === 'asc' ? cmp : -cmp;
    });

  const runTotal = filteredRuns.length;
  const runTotalPages = Math.ceil(runTotal / runPageSize);
  const pagedRuns = filteredRuns.slice((runPage - 1) * runPageSize, runPage * runPageSize);

  const handleExportRuns = () => {
    if (filteredRuns?.length === 0) return;
    setRunExporting(true);
    try {
      const columns = ['file_name', 'status', 'created_at', 'total_videos'];
      const escapeCsv = (v: any) => {
        const s = v == null ? '' : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      };
      const rows = filteredRuns?.map((r: PipelineRun) =>
        [r?.file_name, r?.status, r?.created_at, r?.total_videos || '—']
          .map(escapeCsv).join(',')
      );
      const csv = [columns.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pipeline-run-history-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filteredRuns?.length ?? 0} runs`);
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setRunExporting(false);
    }
  };

  const [insightSearch, setInsightSearch] = useState('');
  const [insightPage, setInsightPage] = useState(1);
  const [insightPageSize, setInsightPageSize] = useState(25);
  const [insightSort, setInsightSort] = useState<InsightSortConfig>({ key: 'thumbs_up_count', direction: 'desc' });
  const [insightExporting, setInsightExporting] = useState(false);

  const INSIGHT_SORTABLE = ['creator_handle', 'framework_name', 'views', 'thumbs_up_count', 'thumbs_down_count', 'created_at'];

  const handleInsightSort = (key: string) => {
    if (!INSIGHT_SORTABLE.includes(key)) return;
    setInsightPage(1);
    setInsightSort((cur) => {
      if (!cur || cur.key !== key || cur.direction === 'desc') return { key, direction: 'asc' };
      return { key, direction: 'desc' };
    });
  };

  const getInsightSortIcon = (key: string) => {
    if (!insightSort || insightSort.key !== key) return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    return insightSort.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const { data: insightData, isLoading: insightLoading } = useQuery({
    queryKey: ['video-insight-feedback', insightSearch, insightPage, insightPageSize, insightSort?.key, insightSort?.direction],
    queryFn: async () => {
      let q = supabase
        .from('trending_videos')
        .select('id, video_id, video_url, creator_handle, framework_name, thumbnail_url, views, thumbs_up_count, thumbs_down_count, created_at', { count: 'exact' })
        .or('thumbs_up_count.gt.0,thumbs_down_count.gt.0');

      const term = insightSearch.trim();
      if (term) {
        q = q.or(`creator_handle.ilike.%${term}%,framework_name.ilike.%${term}%,video_id.ilike.%${term}%`);
      }

      const sortKey = insightSort?.key || 'thumbs_up_count';
      const ascending = insightSort?.direction === 'asc';
      q = q.order(sortKey, { ascending, nullsFirst: false });

      const { data, count, error } = await q.range(
        (insightPage - 1) * insightPageSize,
        insightPage * insightPageSize - 1
      );
      if (error) throw error;
      return { data: data ?? [], total: count ?? 0 };
    },
  });

  const insightVideos = insightData?.data ?? [];
  const insightTotal = insightData?.total ?? 0;
  const insightTotalPages = Math.ceil(insightTotal / insightPageSize);

  const exportInsightFeedback = async () => {
    setInsightExporting(true);
    try {
      const PAGE_SIZE = 1000;
      let allRows: any[] = [];
      let page = 0;

      while (true) {
        let q = supabase
          .from('trending_videos')
          .select('video_id, video_url, creator_handle, framework_name, views, thumbs_up_count, thumbs_down_count, created_at')
          .or('thumbs_up_count.gt.0,thumbs_down_count.gt.0');

        const term = insightSearch.trim();
        if (term) {
          q = q.or(`creator_handle.ilike.%${term}%,framework_name.ilike.%${term}%,video_id.ilike.%${term}%`);
        }

        const sortKey = insightSort?.key || 'thumbs_up_count';
        const ascending = insightSort?.direction === 'asc';
        q = q.order(sortKey, { ascending, nullsFirst: false });

        const { data: pageData, error: pageError } = await q.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (pageError) throw pageError;

        if (pageData && pageData.length > 0) allRows = allRows.concat(pageData);
        if (!pageData || pageData.length < PAGE_SIZE) break;
        page++;
      }

      if (allRows.length === 0) {
        toast.error('No data to export');
        return;
      }

      const columns = ['video_id', 'creator_handle', 'framework_name', 'views', 'thumbs_up_count', 'thumbs_down_count', 'total_feedback', 'up_pct', 'video_url', 'created_at'];

      const escapeCsv = (v: any) => {
        const s = v == null ? '' : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      };

      const rows = allRows.map(r => {
        const total = (r.thumbs_up_count || 0) + (r.thumbs_down_count || 0);
        const upPct = total > 0 ? Math.round((r.thumbs_up_count / total) * 100) : 0;
        return [
          r.video_id, r.creator_handle, r.framework_name, r.views,
          r.thumbs_up_count, r.thumbs_down_count, total, `${upPct}%`,
          r.video_url, r.created_at,
        ].map(escapeCsv).join(',');
      });

      const csv = [columns.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insights-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${allRows.length} rows`);
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setInsightExporting(false);
    }
  };

  const mutation = useMutation({
    mutationFn: async (f: File) => {
      const filePath = `uploads/${f.name}`;
      const { error: uploadError } = await cloudClient.storage
        .from('kalodata-uploads')
        .upload(filePath, f, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: signedData, error: signedError } = await cloudClient.storage
        .from('kalodata-uploads')
        .createSignedUrl(filePath, 3600);
      if (signedError || !signedData?.signedUrl) throw signedError || new Error('Failed to create signed URL');

      return runPipeline({ file_path: signedData.signedUrl, file_name: f.name });
    },
    onSuccess: (data, f) => {
      toast.success('Pipeline started');
      setActiveRunId(data.run_id);
      setRunStartTime(Date.now());
      setFile(null);

      void logAdminAction({
        action:    'CREATE',
        tableName: 'trending_videos',
        recordId:  data.run_id,
        newValues: { status: 'pipeline_started' },
        metadata: {
          event:     'pipeline_started',
          file_name: f.name,
          file_size: f.size,
          run_id:    data.run_id,
        },
      })
    },
    onError: (err: any, f) => {
      toast.error(err.message)

      void logAdminAction({
        action:    'CREATE',
        tableName: 'trending_videos',
        metadata: {
          event:         'pipeline_start_failed',
          file_name:     f.name,
          file_size:     f.size,
          error_message: err.message ?? 'Unknown error',
        },
      })
    },
  });

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && /\.(xlsx|csv)$/i.test(dropped.name)) {
      setFile(dropped);
    } else {
      toast.error('Please upload a .xlsx or .csv file');
    }
  }, []);

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const progressSteps = [
    { key: 'gemini_analyzed', label: 'Gemini Analyzed' },
    { key: 'claude_frameworks', label: 'Claude Frameworks' },
    { key: 'uploaded_to_db', label: 'Uploaded to DB' },
  ];

  const completedSteps = activeRun?.progress
    ? progressSteps.filter((s) => (activeRun.progress as any)?.[s.key]).length
    : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Weekly Top 100</h1>

      <Tabs defaultValue={import.meta.env.VITE_ENV === 'production' || import.meta.env.VITE_ENV === 'staging' ? "pipeline" : "newsletter"} className="w-full">
        <TabsList>
          {/* {import.meta.env.VITE_ENV === 'production' && ( */}
            <TabsTrigger value="pipeline" className="gap-2">
              <Rocket className="h-4 w-4" /> Pipeline
            </TabsTrigger> 
            {/* )} */}
          <TabsTrigger value="newsletter" className="gap-2">
            <Mail className="h-4 w-4" /> Newsletter Queue
          </TabsTrigger>
          <TabsTrigger value="insights-feedback" className="gap-2">
            <ThumbsUp className="h-4 w-4" /> Insights Feedback
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="space-y-6 mt-4">
          {/* Upload zone */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload Kalodata Export</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <Upload className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">
                  {file ? file.name : 'Drag & drop .xlsx or .csv here, or click to browse'}
                </p>
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.csv"
                  className="hidden"
                  onChange={onFileSelect}
                />
              </div>
              {file && (
                <Button
                  className="mt-4"
                  disabled={mutation.isPending || !!activeRunId}
                  onClick={() => mutation.mutate(file)}
                >
                  {mutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Play className="mr-2 h-4 w-4" /> Run Pipeline</>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Active run tracker */}
          {activeRunId && activeRun && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Pipeline Running
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Progress value={(completedSteps / progressSteps.length) * 100} className="h-2" />
                <p className="text-sm text-muted-foreground">
                  {activeRun.progress?.message || activeRun.progress?.current_step || 'Processing...'}
                </p>
                <div className="flex gap-6">
                  {progressSteps.map((step) => {
                    const done = (activeRun.progress as any)?.[step.key];
                    return (
                      <div key={step.key} className="flex items-center gap-2 text-sm">
                        {done ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className={done ? 'text-foreground' : 'text-muted-foreground'}>{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Run history */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <CardTitle className="text-lg">Run History</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search file or status..."
                    value={runSearch}
                    onChange={(e) => { setRunPage(1); setRunSearch(e.target.value); }}
                    className="pl-9 h-8 text-sm"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportRuns}
                  disabled={runExporting || filteredRuns.length === 0}
                >
                  {runExporting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                  ) : (
                    <><Download className="mr-2 h-4 w-4" /> Export</>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : runs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No runs yet</p>
              ) : filteredRuns.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No runs match your search</p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleRunSort('file_name')}>
                            File{getRunSortIcon('file_name')}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleRunSort('status')}>
                            Status{getRunSortIcon('status')}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button className="flex items-center font-medium hover:text-foreground" onClick={() => handleRunSort('created_at')}>
                            Started{getRunSortIcon('created_at')}
                          </button>
                        </TableHead>
                        <TableHead className="w-24" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedRuns?.map((run: PipelineRun) => {
                        const runId = run.run_id || (run as any).id || ''
                        return (
                        <TableRow key={runId || run.file_name}>
                          <TableCell className="font-medium">{run.file_name || '—'}</TableCell>
                          <TableCell>
                            <Badge variant={
                              run.status === 'completed' ? 'default' :
                              run.status === 'failed' ? 'destructive' : 'secondary'
                            }>
                              {run.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {run.created_at ? new Date(run.created_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>
                            {confirmDeleteRunId !== null && confirmDeleteRunId === runId ? (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-red-600 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" /> Sure?
                                </span>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="h-6 px-2 text-xs"
                                  disabled={deleteMutation.isPending}
                                  onClick={() => deleteMutation.mutate(runId)}
                                >
                                  {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => setConfirmDeleteRunId(null)}
                                >
                                  No
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                                onClick={() => runId && setConfirmDeleteRunId(runId)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                      })}
                    </TableBody>
                  </Table>
                  {runTotal > runPageSize && (
                    <DataPagination
                      currentPage={runPage}
                      totalPages={runTotalPages}
                      pageSize={runPageSize}
                      totalItems={runTotal}
                      onPageChange={setRunPage}
                      onPageSizeChange={(s) => { setRunPage(1); setRunPageSize(s); }}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="newsletter" className="mt-4">
          <NewsletterQueue />
        </TabsContent>

        <TabsContent value="insights-feedback" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-lg">Video Insights Feedback</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportInsightFeedback}
                    disabled={insightExporting || insightTotal === 0}
                  >
                    {insightExporting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                    ) : (
                      <><Download className="mr-2 h-4 w-4" /> Export</>
                    )}
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search creator, framework, video ID..."
                      value={insightSearch}
                      onChange={(e) => { setInsightPage(1); setInsightSearch(e.target.value); }}
                      className="pl-9 sm:w-80"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {insightLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : insightVideos.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {insightSearch.trim() ? 'No matching videos found' : 'No feedback yet'}
                </p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Thumbnail</TableHead>
                        <TableHead>
                          <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleInsightSort('creator_handle')}>
                            Creator {getInsightSortIcon('creator_handle')}
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={() => handleInsightSort('framework_name')}>
                            Framework {getInsightSortIcon('framework_name')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-right">
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => handleInsightSort('views')}>
                            Views {getInsightSortIcon('views')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-center">
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => handleInsightSort('thumbs_up_count')}>
                            <ThumbsUp className="h-3.5 w-3.5 text-green-500 mr-1" /> Up {getInsightSortIcon('thumbs_up_count')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-center">
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => handleInsightSort('thumbs_down_count')}>
                            <ThumbsDown className="h-3.5 w-3.5 text-red-500 mr-1" /> Down {getInsightSortIcon('thumbs_down_count')}
                          </Button>
                        </TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead>Video ID</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {insightVideos.map((v: any) => {
                        const total = (v.thumbs_up_count || 0) + (v.thumbs_down_count || 0);
                        const upPct = total > 0 ? Math.round((v.thumbs_up_count / total) * 100) : 0;
                        return (
                          <TableRow key={v.id}>
                            <TableCell>
                              {v.thumbnail_url ? (
                                <img
                                  src={v.thumbnail_url}
                                  alt=""
                                  className="h-10 w-10 rounded object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded bg-muted" />
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap font-medium">
                              {v.creator_handle ? `@${v.creator_handle}` : '—'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {v.framework_name || '—'}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {v.views ? v.views.toLocaleString() : '—'}
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="font-semibold text-green-600">{v.thumbs_up_count || 0}</span>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              <span className="font-semibold text-red-500">{v.thumbs_down_count || 0}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-xs text-muted-foreground">{total}</span>
                                <div className="w-12 h-1.5 rounded-full bg-red-200 overflow-hidden">
                                  <div
                                    className="h-full bg-green-500 rounded-full"
                                    style={{ width: `${upPct}%` }}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {v.video_id || '—'}
                            </TableCell>
                            <TableCell>
                              {v.video_url && (
                                <a
                                  href={v.video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {insightTotal > 0 && (
                    <DataPagination
                      currentPage={insightPage}
                      totalPages={insightTotalPages}
                      pageSize={insightPageSize}
                      totalItems={insightTotal}
                      onPageChange={setInsightPage}
                      onPageSizeChange={(s) => { setInsightPage(1); setInsightPageSize(s); }}
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
